<template>
    <v-dialog v-model="internalVisible" max-width="900px" @click:outside="close">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ $t('lieferant.tvskundenliste.list.title') }}</span>
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-text-field
                    v-model="filterData.suchText"
                    prepend-inner-icon="mdi-magnify"
                    :label="$t('suche')"
                    hide-details
                    variant="outlined"
                    density="comfortable"
                    class="mb-4"
                    @update:model-value="searchFieldInputChanged"
                ></v-text-field>

                <v-infinite-scroll ref="infiniteScroll" @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="tvsKundenListe"
                        :loading="loading"
                        :items-length="totalNumberOfRecords || 0"
                        :must-sort="true"
                        item-value="tvsKundennummer"
                        hover
                        hide-default-footer
                        class="elevation-1 nowhow-table"
                        @update:sort-by="onSortByChanged"
                        @click:row="onRowClicked"
                    >
                        <template #item.tvsKundennummer="{ item }">
                            <TextHighlights :text="item.tvsKundennummer" :search-text="filterData.suchText" />
                        </template>
                        <template #item.name="{ item }">
                            <TextHighlights :text="item.name" :search-text="filterData.suchText" />
                        </template>
                        <template #item.vorname="{ item }">
                            <TextHighlights :text="item.vorname" :search-text="filterData.suchText" />
                        </template>
                        <template #item.firma="{ item }">
                            <TextHighlights :text="item.firma" :search-text="filterData.suchText" />
                        </template>
                        <template #item.plz="{ item }">
                            <TextHighlights :text="item.plz" :search-text="filterData.suchText" />
                        </template>
                        <template #item.ort="{ item }">
                            <TextHighlights :text="item.ort" :search-text="filterData.suchText" />
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import { Component, Vue, Watch, Prop, Emit } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { TvsKundeListApiDto } from '@/api-schema';

import { NAME as NAME_LIEFERANT } from '../store';
const lieferantModul = namespace(NAME_LIEFERANT);

import { GET_TVS_KUNDEN_LISTE, GET_TVS_KUNDEN_LOADING, GET_TVS_KUNDEN_TOTAL_NUMBER_OF_RECORDS } from '../store/getters';
import { APPLY_TVS_KUNDEN_FILTER, CLEAR_TVS_KUNDEN_FILTER } from '../store/mutations';
import { GET_NEXT_TVS_KUNDE, LOAD_TVS_KUNDEN } from '@/areas/lieferant/store/actions';

import { TeilnehmerFilterDto } from '@/types/TeilnehmerFilterDto';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';

@Component({
    components: {
        TextHighlights
    }
})
export default class CustomerPickerDialog extends Vue {
    @Prop({ default: false }) visible!: boolean;

    @lieferantModul.Action(LOAD_TVS_KUNDEN) loadTvsKunden!: Function;
    @lieferantModul.Action(GET_NEXT_TVS_KUNDE) getNextTvsKunden!: Function;
    @lieferantModul.Getter(GET_TVS_KUNDEN_LISTE) tvsKundenListe!: Array<TvsKundeListApiDto>;
    @lieferantModul.Getter(GET_TVS_KUNDEN_LOADING) loading!: boolean;
    @lieferantModul.Getter(GET_TVS_KUNDEN_TOTAL_NUMBER_OF_RECORDS) totalNumberOfRecords!: number;
    @lieferantModul.Mutation(CLEAR_TVS_KUNDEN_FILTER) clearFilter!: Function;
    @lieferantModul.Mutation(APPLY_TVS_KUNDEN_FILTER) applyFilters!: Function;

    internalVisible = false;
    infiniteLoad = null;
    sortBy: any = [{ key: 'tvsKundennummer', order: 'desc' }];
    filterData: TeilnehmerFilterDto = new TeilnehmerFilterDto();

    @Watch('visible', { immediate: true })
    onVisibleChanged(newVal: boolean) {
        this.internalVisible = newVal;
        if (newVal) {
            this.resetAndLoad();
        }
    }

    @Emit('update:visible')
    close() {
        this.internalVisible = false;
        return false;
    }

    @Emit('select')
    onRowClicked(event: any, { item }: any) {
        this.close();
        return item;
    }

    get headers(): Array<any> {
        return [
            { title: this.$t('table.header.tvs.nr'), key: 'tvsKundennummer' },
            { title: this.$t('einstellungen.benutzer.table.name'), key: 'name' },
            { title: this.$t('einstellungen.benutzer.table.vorname'), key: 'vorname' },
            { title: this.$t('taketeilnehmer.table.firma'), key: 'firma' },
            { title: this.$t('benutzerSuche.table.plz'), key: 'plz' },
            { title: this.$t('benutzerSuche.table.ort'), key: 'ort' }
        ];
    }

    searchFieldInputChanged!: () => void;

    created() {
        this.searchFieldInputChanged = debounce(() => {
            this.resetAndLoad();
        }, 400);
    }

    resetAndLoad() {
        this.filterData.status = 'taketeilnehmer.status.aktiv';
        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    async load({ done }: any) {
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            done('ok');
            return;
        }

        this.applyFilters(this.filterData);
        await this.getNextTvsKunden();

        if (this.tvsKundenListe.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0];
            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }

        this.applyFilters(this.filterData);
        this.loadTvsKunden();
    }

    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }
}
</script>

<style scoped>
.nowhow-table {
    cursor: pointer;
}
</style>
